import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { UserRole } from '../../../../../types';
import { useForm, Controller } from 'react-hook-form';

import { handleErrors } from '../../../../utils/errors';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { UserKeys, updateRole } from '../../../../api/users-api';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import UsersAutocomplete from '../../../../shared/users-autocomplete';
import AvatarChip from '../../../../shared/avatar-chip';
import OrganizationsAutoComplete from '../../../../shared/organizations-autocomplete';
import DomainsAutoComplete from '../../../../shared/domain-autocomplete';
import { Messages } from '../../../../utils/message-constants';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface RoleLimitedVisibilityFormProps {
  changeLogRoles: string[];
  role: UserRole;
  domains: string[] | undefined;
  roles: UserRole[];
}

export default function RoleLimitedVisibilityForm({
  changeLogRoles,
  role,
  domains,
  roles,
}: RoleLimitedVisibilityFormProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const autocompleteKeyOrgs = useRef(new Date().getTime().toString());
  const [autoCompleteResponseOrgs, setAutoCompleteResponseOrgs] = useState('');
  const autocompleteKeyDomains = useRef(new Date().getTime().toString());
  const autocompleteKeyGroupDomains = useRef(new Date().getTime().toString());
  const [autoCompleteResponseDomains, setAutoCompleteResponseDomains] =
    useState('');
  const [
    autoCompleteResponseGroupDomains,
    setAutoCompleteResponseGroupDomains,
  ] = useState('');
  const [autoCompleteResponseAllowList, setAutoCompleteResponseAllowList] =
    useState('');
  const [autoCompleteResponseBlockList, setAutoCompleteResponseBlockList] =
    useState('');
  const autocompleteKeyRoleList = useRef(new Date().getTime().toString());
  const [autoCompleteResponseRoleList, setAutoCompleteResponseRoleList] =
    useState('');
  const [isSourceError, setIsSourceError] = useState(false);

  const queryClient = useQueryClient();

  const { mutate, isPending: updatingRole } = useMutation({
    mutationFn: (data: UserRole) => updateRole(data, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Role limited visibility settings'),
      });

      queryClient.setQueryData([UserKeys.Roles], (oldData: UserRole[]) => {
        if (!oldData) {
          return;
        } else {
          const filteredList = oldData.filter(
            (currentRole) => currentRole.roleName !== role.roleName
          );
          return [...filteredList, data];
        }
      });
      reset();
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating(
          'the role limited visibility settings'
        ),
      });
    },
    onSettled: (_returnData, _error, data) => {
      createEvent(
        analytics.administration.role_details.update_role_limited_visibility,
        {
          module: ModuleList.modules.administration.name,
          section: ModuleList.modules.administration.sections.roleManager,
          roleType: data.roleType,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isLoading },
    getValues,
    setValue,
    watch,
    reset,
  } = useForm<UserRole>({
    values: {
      ...role,
      limitedVisOrgList: role.limitedVisOrgList ? role.limitedVisOrgList : [],
      limitedVisRoleList: role.limitedVisRoleList
        ? role.limitedVisRoleList
        : [],
      limitedVisAllowList: role.limitedVisAllowList
        ? role.limitedVisAllowList
        : [],
      limitedVisBlockList: role.limitedVisBlockList
        ? role.limitedVisBlockList
        : [],
      limitedVisDomainList: role.limitedVisDomainList
        ? role.limitedVisDomainList
        : [],
      limitedGroupVisDomainList: role.limitedGroupVisDomainList
        ? role.limitedGroupVisDomainList
        : [],
    },
  });

  const onSubmit = (data: UserRole) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, AdministrationRoles.EditRole.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      if (combinedSourcesLength === 0 && limitedVisEnabled) {
        setIsSourceError(true);
      } else {
        setIsSourceError(false);
        mutate(data);
      }
    }
  };

  const limitedVisEnabled = watch('limitedVisEnabled');
  const limitedVisOrgList = watch('limitedVisOrgList') || [];
  const limitedVisAllowList = watch('limitedVisAllowList') || [];
  const limitedVisBlockList = watch('limitedVisBlockList') || [];
  const limitedVisDomainList = watch('limitedVisDomainList') || [];
  const limitedGroupVisDomainList = watch('limitedGroupVisDomainList') || [];
  const limitedVisRoleList = watch('limitedVisRoleList') || [];
  const roleNames = roles.map((role) => role.roleName);

  const combinedSourcesLength =
    limitedVisOrgList.length +
    limitedVisRoleList.length +
    limitedVisDomainList.length +
    limitedVisAllowList.length +
    limitedVisBlockList.length +
    limitedGroupVisDomainList.length;

  return (
    <Box
      component="form"
      sx={{ position: 'relative', height: '100%' }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Stack
        flexDirection="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
        sx={{ position: 'absolute', top: -50, right: -15 }}
      >
        <Button
          variant="contained"
          disabled={!isDirty || updatingRole}
          loading={updatingRole || isLoading}
          sx={{ position: 'absolute', top: 0, right: 0 }}
          type="submit"
        >
          Save
        </Button>
      </Stack>

      <Box sx={{ height: '100%', overflowY: 'auto' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 300 }}>
          Enable Limited Visibility for this Role
        </Typography>
        <Box sx={{ px: 4 }}>
          <Controller
            name="limitedVisEnabled"
            control={control}
            render={({ field: props }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...props}
                    checked={props.value}
                    onChange={(e) => props.onChange(e.target.checked)}
                  />
                }
                label="Enable Limited Visibility"
              />
            )}
          />
        </Box>
        <Divider sx={{ mt: 1 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Summary of all sources currently included in the limited visibility
          list of this role:
        </Typography>
        {limitedVisOrgList.length > 0 ? (
          <>
            <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 1 }}>
              Orgs:
            </Typography>
            <Stack sx={{ px: 4 }} gap={1}>
              <Stack flexDirection="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {limitedVisOrgList.length > 0
                  ? limitedVisOrgList.map((org) => {
                      return (
                        <AvatarChip
                          key={org}
                          label={org}
                          type={'OrgUnit'}
                          email={org}
                          onDelete={() => {
                            if (limitedVisEnabled) {
                              setValue(
                                'limitedVisOrgList',
                                limitedVisOrgList.filter(
                                  (limitedOrg) => limitedOrg !== org
                                ),
                                {
                                  shouldDirty: true,
                                }
                              );
                            }
                          }}
                        />
                      );
                    })
                  : null}
              </Stack>
            </Stack>
          </>
        ) : null}
        {limitedVisRoleList.length > 0 ? (
          <>
            <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 1 }}>
              Roles:
            </Typography>
            <Stack sx={{ px: 4 }} gap={1}>
              <Stack flexDirection="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {limitedVisRoleList.length > 0
                  ? limitedVisRoleList.map((role) => {
                      return (
                        <AvatarChip
                          key={role}
                          type="Roles"
                          email={role}
                          size={'small'}
                          onDelete={() => {
                            if (limitedVisRoleList) {
                              setValue(
                                'limitedVisRoleList',
                                limitedVisRoleList.filter(
                                  (limitedRole) => limitedRole !== role
                                ),
                                {
                                  shouldDirty: true,
                                }
                              );
                            }
                          }}
                        />
                      );
                    })
                  : null}
              </Stack>
            </Stack>
          </>
        ) : null}
        {limitedVisDomainList.length > 0 ? (
          <>
            <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 1 }}>
              Domains:
            </Typography>
            <Stack sx={{ px: 4 }} gap={1}>
              <Stack flexDirection="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {limitedVisDomainList.length > 0
                  ? limitedVisDomainList.map((domain) => {
                      return (
                        <AvatarChip
                          key={domain}
                          label={domain}
                          type={'Domain'}
                          email={domain}
                          onDelete={() => {
                            if (limitedVisEnabled) {
                              setValue(
                                'limitedVisDomainList',
                                limitedVisDomainList.filter(
                                  (limitedDomain) => limitedDomain !== domain
                                ),
                                {
                                  shouldDirty: true,
                                }
                              );
                            }
                          }}
                        />
                      );
                    })
                  : null}
              </Stack>
            </Stack>
          </>
        ) : null}
        {limitedGroupVisDomainList.length > 0 ? (
          <>
            <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 1 }}>
              Group Domains:
            </Typography>
            <Stack sx={{ px: 4 }} gap={1}>
              <Stack flexDirection="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {limitedGroupVisDomainList.length > 0
                  ? limitedGroupVisDomainList.map((domain) => {
                      return (
                        <AvatarChip
                          key={domain}
                          label={domain}
                          type={'Domain'}
                          email={domain}
                          onDelete={() => {
                            if (limitedVisEnabled) {
                              setValue(
                                'limitedGroupVisDomainList',
                                limitedGroupVisDomainList.filter(
                                  (limitedDomain) => limitedDomain !== domain
                                ),
                                {
                                  shouldDirty: true,
                                }
                              );
                            }
                          }}
                        />
                      );
                    })
                  : null}
              </Stack>
            </Stack>
          </>
        ) : null}
        {limitedVisAllowList.length > 0 ? (
          <>
            <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 1 }}>
              Users:
            </Typography>
            <Stack sx={{ px: 4 }} gap={1}>
              <Stack flexDirection="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {limitedVisAllowList.length > 0
                  ? limitedVisAllowList.map((user) => {
                      return (
                        <AvatarChip
                          key={user}
                          label={user}
                          type={'User'}
                          email={user}
                          onDelete={() => {
                            if (limitedVisEnabled) {
                              setValue(
                                'limitedVisAllowList',
                                limitedVisAllowList.filter(
                                  (limitedUser) => limitedUser !== user
                                ),
                                {
                                  shouldDirty: true,
                                }
                              );
                            }
                          }}
                        />
                      );
                    })
                  : null}
              </Stack>
            </Stack>
          </>
        ) : null}
        {combinedSourcesLength === 0 ? (
          <Stack sx={{ pt: 1, px: 4 }} gap={1}>
            {!isSourceError ? (
              <Typography sx={{ fontSize: 16, fontWeight: 300 }}>
                No sources selected.
              </Typography>
            ) : (
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 300,
                  color: (theme) => theme.palette.error.main,
                }}
              >
                No sources selected. Please select sources to enable limited
                visibility.
              </Typography>
            )}
          </Stack>
        ) : null}
        {limitedVisBlockList.length > 0 ? (
          <>
            <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 1 }}>
              Excluding these users:
            </Typography>
            <Stack sx={{ px: 4 }} gap={1}>
              {limitedVisBlockList.length > 0 ? (
                <Stack
                  flexDirection="row"
                  gap={1}
                  flexWrap="wrap"
                  sx={{ mt: 1 }}
                >
                  {limitedVisBlockList.map((user) => {
                    return (
                      <AvatarChip
                        key={user}
                        label={user}
                        type={'User'}
                        email={user}
                        onDelete={() => {
                          if (limitedVisEnabled) {
                            setValue(
                              'limitedVisBlockList',
                              limitedVisBlockList.filter(
                                (nonLimitedUser) => nonLimitedUser !== user
                              ),
                              {
                                shouldDirty: true,
                              }
                            );
                          }
                        }}
                      />
                    );
                  })}
                </Stack>
              ) : null}
            </Stack>
          </>
        ) : null}
        <Divider sx={{ mt: 1 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Orgs
        </Typography>
        <Stack sx={{ pt: 1, px: 4 }} gap={1}>
          <Typography variant="body2" sx={{ fontWeight: 300 }}>
            Add Orgs to the limited visibility list of this role
          </Typography>
          <Stack flexDirection="row" gap={1}>
            <Box sx={{ width: 400 }}>
              <OrganizationsAutoComplete
                newKey={autocompleteKeyOrgs.current}
                autoCompleteValue={autoCompleteResponseOrgs}
                setAutoCompleteValue={setAutoCompleteResponseOrgs}
                disabled={!limitedVisEnabled}
                onSelection={(value) => {
                  if (!value) return;
                  if (limitedVisOrgList) {
                    if (limitedVisOrgList.includes(value)) return;
                    setValue(
                      'limitedVisOrgList',
                      [...limitedVisOrgList!, value],
                      {
                        shouldDirty: true,
                      }
                    );
                  } else {
                    setValue('limitedVisOrgList', [value], {
                      shouldDirty: true,
                    });
                  }
                  autocompleteKeyOrgs.current = new Date().getTime().toString();
                  setAutoCompleteResponseOrgs('');
                }}
              />
            </Box>
          </Stack>
          {limitedVisOrgList.length > 0 ? (
            <Stack flexDirection="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              {limitedVisOrgList.length > 0
                ? limitedVisOrgList.map((org) => {
                    return (
                      <AvatarChip
                        key={org}
                        label={org}
                        type={'OrgUnit'}
                        email={org}
                        onDelete={() => {
                          if (limitedVisEnabled) {
                            setValue(
                              'limitedVisOrgList',
                              limitedVisOrgList.filter(
                                (limitedOrg) => limitedOrg !== org
                              ),
                              {
                                shouldDirty: true,
                              }
                            );
                          }
                        }}
                      />
                    );
                  })
                : null}
            </Stack>
          ) : null}
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Roles
        </Typography>
        <Stack
          sx={{
            pt: 1,
            px: 4,
          }}
          gap={1}
        >
          <Typography variant="body2" sx={{ fontWeight: 300 }}>
            Add roles to the limited visibility list of this role
          </Typography>
          <Stack flexDirection="row" gap={1}>
            <Autocomplete
              id="roles"
              key={autocompleteKeyRoleList.current}
              options={roleNames}
              disabled={!limitedVisEnabled}
              autoHighlight
              value={autoCompleteResponseRoleList}
              onChange={(_event, value) => {
                if (!value) {
                  setAutoCompleteResponseRoleList('');
                } else {
                  setAutoCompleteResponseRoleList(value);
                  if (limitedVisRoleList) {
                    if (limitedVisRoleList.includes(value)) return;
                    setValue(
                      'limitedVisRoleList',
                      [...limitedVisRoleList!, value],
                      {
                        shouldDirty: true,
                      }
                    );
                  } else {
                    setValue('limitedVisRoleList', [value], {
                      shouldDirty: true,
                    });
                  }
                  autocompleteKeyRoleList.current = new Date()
                    .getTime()
                    .toString();
                  setAutoCompleteResponseRoleList('');
                }
              }}
              sx={{ width: 400 }}
              renderInput={(params) => (
                <TextField {...params} label="Search for a role" />
              )}
            />
          </Stack>
          {limitedVisRoleList.length > 0 ? (
            <Stack flexDirection="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              {limitedVisRoleList.length > 0
                ? limitedVisRoleList.map((role) => {
                    return (
                      <AvatarChip
                        key={role}
                        type="Roles"
                        email={role}
                        size={'small'}
                        onDelete={() => {
                          if (limitedVisRoleList) {
                            setValue(
                              'limitedVisRoleList',
                              limitedVisRoleList.filter(
                                (limitedRole) => limitedRole !== role
                              ),
                              {
                                shouldDirty: true,
                              }
                            );
                          }
                        }}
                      />
                    );
                  })
                : null}
            </Stack>
          ) : null}
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Domains
        </Typography>
        <Stack sx={{ pt: 1, px: 4 }} gap={1}>
          <Typography variant="body2" sx={{ fontWeight: 300 }}>
            Add Domains to the limited visibility list of this role
          </Typography>
          <Stack flexDirection="row" gap={1}>
            <Box sx={{ width: 400 }}>
              <DomainsAutoComplete
                newKey={autocompleteKeyDomains.current}
                size="medium"
                autoCompleteValue={autoCompleteResponseDomains}
                setAutoCompleteValue={setAutoCompleteResponseDomains}
                domains={domains || []}
                disabled={!limitedVisEnabled}
                onSelection={(value) => {
                  if (!value) return;
                  if (limitedVisDomainList) {
                    if (limitedVisDomainList.includes(value)) return;
                    setValue(
                      'limitedVisDomainList',
                      [...limitedVisDomainList!, value],
                      {
                        shouldDirty: true,
                      }
                    );
                  } else {
                    setValue('limitedVisDomainList', [value], {
                      shouldDirty: true,
                    });
                  }
                  autocompleteKeyDomains.current = new Date()
                    .getTime()
                    .toString();
                  setAutoCompleteResponseDomains('');
                }}
              />
            </Box>
          </Stack>
          {limitedVisDomainList.length > 0 ? (
            <Stack flexDirection="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              {limitedVisDomainList.length > 0
                ? limitedVisDomainList.map((domain) => {
                    return (
                      <AvatarChip
                        key={domain}
                        label={domain}
                        type={'Domain'}
                        email={domain}
                        onDelete={() => {
                          if (limitedVisEnabled) {
                            setValue(
                              'limitedVisDomainList',
                              limitedVisDomainList.filter(
                                (limitedDomain) => limitedDomain !== domain
                              ),
                              {
                                shouldDirty: true,
                              }
                            );
                          }
                        }}
                      />
                    );
                  })
                : null}
            </Stack>
          ) : null}
        </Stack>
        <Stack sx={{ pt: 1, px: 4 }} gap={1}>
          <Typography variant="body2" sx={{ fontWeight: 300 }}>
            Add Domains for group limited visibility. Only those groups in the
            below domains will be visible to users of this role.
          </Typography>
          <Stack flexDirection="row" gap={1}>
            <Box sx={{ width: 400 }}>
              <DomainsAutoComplete
                newKey={autocompleteKeyGroupDomains.current}
                size="medium"
                autoCompleteValue={autoCompleteResponseGroupDomains}
                setAutoCompleteValue={setAutoCompleteResponseGroupDomains}
                domains={domains || []}
                disabled={!limitedVisEnabled}
                onSelection={(value) => {
                  if (!value) return;
                  if (limitedGroupVisDomainList) {
                    if (limitedGroupVisDomainList.includes(value)) return;
                    setValue(
                      'limitedGroupVisDomainList',
                      [...limitedGroupVisDomainList!, value],
                      {
                        shouldDirty: true,
                      }
                    );
                  } else {
                    setValue('limitedGroupVisDomainList', [value], {
                      shouldDirty: true,
                    });
                  }
                  autocompleteKeyGroupDomains.current = new Date()
                    .getTime()
                    .toString();
                  setAutoCompleteResponseGroupDomains('');
                }}
              />
            </Box>
          </Stack>
          {limitedGroupVisDomainList.length > 0 ? (
            <Stack flexDirection="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              {limitedGroupVisDomainList.length > 0
                ? limitedGroupVisDomainList.map((domain) => {
                    return (
                      <AvatarChip
                        key={domain}
                        label={domain}
                        type={'Domain'}
                        email={domain}
                        onDelete={() => {
                          if (limitedVisEnabled) {
                            setValue(
                              'limitedGroupVisDomainList',
                              limitedGroupVisDomainList.filter(
                                (limitedDomain) => limitedDomain !== domain
                              ),
                              {
                                shouldDirty: true,
                              }
                            );
                          }
                        }}
                      />
                    );
                  })
                : null}
            </Stack>
          ) : null}
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Users to Include
        </Typography>
        <Stack sx={{ pt: 1, px: 4 }} gap={1}>
          <Typography variant="body2" sx={{ fontWeight: 300 }}>
            Add users to the limited visibility list of this role
          </Typography>
          <Stack flexDirection="row" gap={1}>
            <Box sx={{ width: 400 }}>
              <UsersAutocomplete
                autocompleteValue={autoCompleteResponseAllowList}
                setAutoCompleteValue={setAutoCompleteResponseAllowList}
                disabled={!limitedVisEnabled}
                onSelection={(value) => {
                  if (!value) return;
                  if (limitedVisAllowList) {
                    if (limitedVisAllowList.includes(value)) return;
                    setValue(
                      'limitedVisAllowList',
                      [...limitedVisAllowList!, value],
                      {
                        shouldDirty: true,
                      }
                    );
                  } else {
                    setValue('limitedVisAllowList', [value], {
                      shouldDirty: true,
                    });
                  }
                  setAutoCompleteResponseAllowList('');
                }}
              />
            </Box>
          </Stack>
          {limitedVisAllowList.length > 0 ? (
            <Stack flexDirection="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              {limitedVisAllowList.length > 0
                ? limitedVisAllowList.map((user) => {
                    return (
                      <AvatarChip
                        key={user}
                        label={user}
                        type={'User'}
                        email={user}
                        onDelete={() => {
                          if (limitedVisEnabled) {
                            setValue(
                              'limitedVisAllowList',
                              limitedVisAllowList.filter(
                                (limitedUser) => limitedUser !== user
                              ),
                              {
                                shouldDirty: true,
                              }
                            );
                          }
                        }}
                      />
                    );
                  })
                : null}
            </Stack>
          ) : null}
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Users to Exclude
        </Typography>
        <Stack sx={{ pt: 1, px: 4 }} gap={1}>
          <Typography variant="body2" sx={{ fontWeight: 300 }}>
            Exclude users from the limited visibility list of this role
          </Typography>
          <Stack flexDirection="row" gap={1}>
            <Box sx={{ width: 400 }}>
              <UsersAutocomplete
                autocompleteValue={autoCompleteResponseBlockList}
                setAutoCompleteValue={setAutoCompleteResponseBlockList}
                disabled={!limitedVisEnabled}
                onSelection={(value) => {
                  if (!value) return;
                  if (limitedVisBlockList) {
                    if (limitedVisBlockList.includes(value)) return;
                    setValue(
                      'limitedVisBlockList',
                      [...limitedVisBlockList!, value],
                      {
                        shouldDirty: true,
                      }
                    );
                  } else {
                    setValue('limitedVisBlockList', [value], {
                      shouldDirty: true,
                    });
                  }
                  setAutoCompleteResponseBlockList('');
                }}
              />
            </Box>
          </Stack>
          {limitedVisBlockList.length > 0 ? (
            <Stack flexDirection="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              {limitedVisBlockList.map((user) => {
                return (
                  <AvatarChip
                    key={user}
                    label={user}
                    type={'User'}
                    email={user}
                    onDelete={() => {
                      if (limitedVisEnabled) {
                        setValue(
                          'limitedVisBlockList',
                          limitedVisBlockList.filter(
                            (nonLimitedUser) => nonLimitedUser !== user
                          ),
                          {
                            shouldDirty: true,
                          }
                        );
                      }
                    }}
                  />
                );
              })}
            </Stack>
          ) : null}
        </Stack>
      </Box>
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          const newRole = getValues();
          mutate(newRole);
        }}
        mutateLoading={updatingRole}
      />
    </Box>
  );
}
